/**
 * Where is this React app running?
 *
 * The phone app is the SAME bundle as the website, wrapped in a Capacitor shell (see
 * /mobile at the repo root). The native runtime injects `window.Capacitor` before our code
 * runs, so we can detect the shell WITHOUT adding @capacitor/core to emofront's deps (which
 * would touch yarn.lock and the Render build). Native plugins are reached the same way later:
 * `window.Capacitor.Plugins.<Name>` for plugins installed in /mobile.
 */
export const isNativeApp = () => {
  if (typeof window === "undefined") return false;
  const cap = window.Capacitor;
  try {
    return !!(cap && typeof cap.isNativePlatform === "function" && cap.isNativePlatform());
  } catch (e) {
    return false;
  }
};

/** Web path of the phone flows (for testing in a phone browser against the dev server). */
export const MOBILE_BASE = "/m";

/** Inside the shell the phone flows ARE the app (root); on the web they live under /m. */
export const mobileBase = () => (isNativeApp() ? "" : MOBILE_BASE);

/**
 * A short buzz for a physical-feeling action (keeping a clip, grabbing a trim handle).
 * `navigator.vibrate` works in the Android WebView, so this needs no plugin, and it is a
 * no-op wherever vibration is unavailable or the user has it switched off.
 */
export const buzz = (ms = 8) => {
  try { if (typeof navigator !== "undefined" && navigator.vibrate) navigator.vibrate(ms); }
  catch (e) { /* never worth surfacing */ }
};

/** The three looks the clip list can take. Stored per phone, not per project. */
export const LOOKS = ["cards", "feed", "list"];
const LOOK_KEY = "cc_mobile_look";

export const readLook = () => {
  try {
    const v = window.localStorage.getItem(LOOK_KEY);
    return LOOKS.includes(v) ? v : "cards";
  } catch (e) { return "cards"; }
};

export const writeLook = (look) => {
  try { window.localStorage.setItem(LOOK_KEY, look); } catch (e) { /* private mode */ }
};

/**
 * A small, sweet "pop" when a clip is kept — a cherry being picked.
 *
 * Synthesised with Web Audio rather than shipped as a file: it is a few lines, adds nothing to
 * the download, and can be tuned by ear in code. A quick upward pitch bend with a very fast
 * decay reads as a pop rather than a beep. The context is created lazily on the first real tap,
 * because browsers refuse to start audio before a gesture, and every call is wrapped so a phone
 * with audio blocked simply stays silent.
 */
let audioCtx = null;
let unlocked = false;

/**
 * Browsers will not start audio outside a user gesture, and Android's WebView is stricter than
 * most: a context created inside a React handler can still come up "suspended", and a sound
 * scheduled on a suspended context is simply dropped — silently, which is exactly how the pop
 * went missing. So the context is created and resumed on the FIRST touch anywhere in the app,
 * long before anything wants to make a noise.
 */
export function unlockAudio() {
  if (unlocked) return;
  try {
    const Ctx = window.AudioContext || window.webkitAudioContext;
    if (!Ctx) return;
    if (!audioCtx) audioCtx = new Ctx();
    if (audioCtx.state === "suspended") audioCtx.resume();
    // A zero-volume blip: some versions only truly start the clock once something has played.
    const osc = audioCtx.createOscillator();
    const gain = audioCtx.createGain();
    gain.gain.value = 0;
    osc.connect(gain).connect(audioCtx.destination);
    osc.start();
    osc.stop(audioCtx.currentTime + 0.01);
    unlocked = true;
  } catch (e) { /* silence is an acceptable outcome */ }
}

if (typeof window !== "undefined") {
  const once = () => {
    unlockAudio();
    window.removeEventListener("pointerdown", once);
    window.removeEventListener("touchstart", once);
  };
  window.addEventListener("pointerdown", once, { passive: true });
  window.addEventListener("touchstart", once, { passive: true });
}

/** A small, sweet "pop" when a clip is kept — a cherry being picked. */
export function pop() {
  try {
    if (!audioCtx) unlockAudio();
    if (!audioCtx) return;
    if (audioCtx.state === "suspended") audioCtx.resume();

    const t = audioCtx.currentTime + 0.005;
    const osc = audioCtx.createOscillator();
    const gain = audioCtx.createGain();

    osc.type = "sine";
    osc.frequency.setValueAtTime(420, t);
    osc.frequency.exponentialRampToValueAtTime(900, t + 0.055);   // the bend that makes it "pop"

    gain.gain.setValueAtTime(0.0001, t);
    gain.gain.exponentialRampToValueAtTime(0.3, t + 0.012);
    gain.gain.exponentialRampToValueAtTime(0.0001, t + 0.14);

    osc.connect(gain).connect(audioCtx.destination);
    osc.start(t);
    osc.stop(t + 0.16);
  } catch (e) { /* silence is an acceptable outcome */ }
}

/** Keeping a clip should feel like picking one: a buzz and a pop together. */
export const pick = () => { buzz(12); pop(); };
